<script lang="ts" setup>
import { computed, type PropType, toRef } from 'vue'

import CrossSectionDetailTableRow from '@/components/calculation-result/CrossSectionDetailTableRow.vue'
import { useCrossSectionStore } from '@/stores/crossSection'
import type { CrossSectionType } from '@/stores/type/crossSection.type'
import { storeToRefs } from 'pinia'
import { formatValue } from '@/components/calculation-result/utils/formatValue'

const crossSectionStore = useCrossSectionStore()

const { availableInfo, fields } = storeToRefs(crossSectionStore)

const props = defineProps({
  item: { type: Object as PropType<CrossSectionType>, required: true },
  tableHeight: { type: Number, default: 60 }
})

const item = toRef(props, 'item')

const getUnit = (param: string): string => {
  const label = fields.value ? fields.value[param]?.label : ''
  let unit = '-'
  if (label.includes('[')) {
    unit = label.split('[')[1].split(']')[0]
  }
  return unit
}

const getChoices = (key: keyof CrossSectionType) => {
  return fields.value ? (fields.value[key]?.choices ?? []) : []
}

const getValue = (key: keyof CrossSectionType): string => {
  const choiceField = ['filter_unit', 'min_required_grading']
  if (choiceField.some((field) => key.includes(field))) {
    return getChoices(key).find((choice) => choice.value === item.value[key])?.display_name ?? ''
  }
  return formatValue(item.value[key])
}

const NORMATIVE_PREFIX = 'normative_'
const MAIN_ENGINE_PREFIX = 'main_'
const THRUSTER_PREFIX = 'transverse_thruster_'

const getRowValue = (param: string | null): { text: string; unit: string; value: any } | null => {
  if (!param) return null
  const field = NORMATIVE_PREFIX + param
  const label = fields.value ? fields.value[field]?.label : ''
  const text = label ? label.split('(')[0] : ''
  const unit = getUnit(field)
  const value = [NORMATIVE_PREFIX, MAIN_ENGINE_PREFIX, THRUSTER_PREFIX].map((prefix) => {
    const key = (prefix + param) as keyof CrossSectionType
    return getValue(key)
  })
  if (value.every((v) => v === '')) return null
  return { text, unit, value }
}

const tableRowFields = computed<(string | null)[]>(() => {
  const rowFields = [
    'protection_width',
    'protection_length',
    'total_area',
    'engine',
    'min_required_grading',
    'effective_safety_factor',
    'average_rock_layer_thickness',
    'd_gabion',
    'm50_gabion',
    'd_blockmat',
    'd_block',
    'd_mattress',
    'insitu_filled_mattress_thickness',
    'filter_unit',
    'filter_unit_height',
    'filter_unit_diameter',
    'filter_unit_volume',
    'filter_unit_layer_thickness'
  ]
  return rowFields.filter((field) => availableInfo.value.includes(field))
})
</script>

<template>
  <div class="d-flex flex-column fill-height">
    <v-table class="overflow-auto" density="compact" fixed-header hover>
      <thead>
        <tr>
          <th class="text-center border-top-grey pl-0" style="width: 58%">Field</th>
          <th class="text-center border-left-grey border-top-grey" style="width: 20%">Normative</th>
          <th class="text-center border-left-grey border-top-grey" style="width: 20%">
            Main engine
          </th>
          <th class="text-center border-left-grey border-top-grey" style="width: 20%">
            Transverse thruster
          </th>
        </tr>
      </thead>
      <tbody v-if="item" class="text-center fill-height pb-3">
        <template v-for="(field, idx) in tableRowFields">
          <CrossSectionDetailTableRow
            v-if="getRowValue(field)"
            :key="idx"
            :data="getRowValue(field)"
          />
        </template>
        <tr>
          <td colspan="48" style="height: 0px"></td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="8" style="text-align: center">No data available</td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<style scoped>
th {
  text-align: center;
}
</style>
